import { useState } from 'react';
import { Ban, History, ShieldCheck } from 'lucide-react';
import { Customer } from '@/types/customer-type';
import { Button } from '@/components/ui/button';
import BlockCustomerDialog from './BlockCustomerDialog';

interface BlockedCustomerBannerProps {
  customer: Customer;
  onUpdated?: (updatedCustomer: Customer) => void;
}

/** Red banner shown on customer panels when the customer is blocked, with quick Unblock & History actions. */
export default function BlockedCustomerBanner({
  customer,
  onUpdated,
}: BlockedCustomerBannerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState<'action' | 'history'>('action');

  if (!customer.blocked) return null;

  return (
    <>
      <div className="flex flex-col gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
        <div className="flex items-start gap-2">
          <Ban className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1 text-xs space-y-0.5">
            <p className="font-bold uppercase tracking-wide text-red-800">Blocked Customer</p>
            {customer.blockReason && (
              <p className="break-words text-red-700">
                <span className="font-semibold">Reason:</span> {customer.blockReason}
              </p>
            )}
            {(customer.blockedByName || customer.blockedAt) && (
              <p className="text-[11px] text-red-500">
                {customer.blockedByName ? `By ${customer.blockedByName}` : ''}
                {customer.blockedAt ? ` · ${new Date(customer.blockedAt).toLocaleString()}` : ''}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-red-200/70">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="h-6 px-2 text-[11px] text-red-700 hover:bg-red-100 hover:text-red-900 gap-1"
            onClick={() => {
              setDialogTab('history');
              setDialogOpen(true);
            }}
          >
            <History className="h-3 w-3" />
            History
          </Button>
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="h-6 px-2.5 text-[11px] border-red-300 bg-white text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-300 gap-1"
            onClick={() => {
              setDialogTab('action');
              setDialogOpen(true);
            }}
          >
            <ShieldCheck className="h-3 w-3" />
            Unblock
          </Button>
        </div>
      </div>

      <BlockCustomerDialog
        customer={customer}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultTab={dialogTab}
        onSuccess={onUpdated}
      />
    </>
  );
}

